import { useEffect, useState } from "react";
import { api, picUrl } from "@/lib/api";
import { Zap, Trophy, Crown, Flame } from "lucide-react";

const RANK_ACCENTS = [
  { border: "border-[#39FF14]", glow: "shadow-[0_0_25px_rgba(57,255,20,0.45)]", text: "text-[#39FF14]" },
  { border: "border-[#00FFFF]", glow: "shadow-[0_0_18px_rgba(0,255,255,0.35)]", text: "text-[#00FFFF]" },
  { border: "border-[#FF007F]", glow: "shadow-[0_0_18px_rgba(255,0,127,0.35)]", text: "text-[#FF007F]" },
];

export default function TopBoosters({ username }) {
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState({ boosters: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get(`/users/${username}/top-boosters?period=${period}&limit=5`)
      .then((r) => {
        if (mounted) setData(r.data);
      })
      .catch(() => {
        if (mounted) setData({ boosters: [] });
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [username, period]);

  const boosters = data.boosters || [];

  if (!loading && boosters.length === 0) return null; // hide entirely if no tips

  return (
    <section
      data-testid="top-boosters"
      className="px-6 py-8 border-b border-white/10"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Trophy className="h-4 w-4 text-[#39FF14]" strokeWidth={1.8} />
          <h2 className="font-display font-black text-lg tracking-tight">
            top boosters
          </h2>
          <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">
            // {period === "week" ? "last.7d" : "all.time"}
          </span>
        </div>
        <div className="flex items-center border border-white/15">
          {["week", "all"].map((p) => (
            <button
              key={p}
              data-testid={`top-boosters-period-${p}`}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${
                period === p
                  ? "bg-[#39FF14] text-black font-bold"
                  : "text-white/50 hover:text-white"
              }`}
            >
              {p === "week" ? "week" : "all-time"}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <div className="h-6 w-6 border-2 border-[#39FF14] border-t-transparent animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {boosters.map((b) => {
            const accent = RANK_ACCENTS[b.rank - 1];
            return (
              <div
                key={`${b.rank}-${b.name}`}
                data-testid={`top-booster-rank-${b.rank}`}
                className={`flex items-center gap-3 p-3 bg-[#0A0A0C] border ${
                  accent ? accent.border : "border-white/10"
                } ${accent ? accent.glow : ""}`}
              >
                <span
                  className={`font-display font-black text-2xl w-8 text-center ${
                    accent ? accent.text : "text-white/30"
                  }`}
                >
                  #{b.rank}
                </span>
                {b.picture ? (
                  <img
                    src={picUrl(b.picture)}
                    alt=""
                    className="h-10 w-10 object-cover border border-white/20"
                  />
                ) : (
                  <div className="h-10 w-10 bg-[#FF007F]/20 flex items-center justify-center font-display font-bold text-sm text-[#FF007F] border border-[#FF007F]/40">
                    {(b.name || "?")[0]?.toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white text-sm truncate flex items-center gap-1.5">
                    {b.rank === 1 && (
                      <Crown className="h-3.5 w-3.5 text-[#39FF14]" fill="#39FF14" strokeWidth={0} />
                    )}
                    {b.name}
                    {b.is_guest && (
                      <span className="font-mono text-[9px] uppercase tracking-widest text-white/30">
                        guest
                      </span>
                    )}
                  </p>
                  <div className="flex items-center gap-2">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                      {b.tip_count} boost{b.tip_count === 1 ? "" : "s"}
                    </p>
                    {b.has_streak && (
                      <span
                        data-testid={`streak-badge-${b.rank}`}
                        title={`On a ${b.streak_count}-video boost streak`}
                        className="flex items-center gap-0.5 px-1.5 py-0.5 border border-[#FF6B00]/60 text-[#FF6B00] font-mono text-[9px] uppercase tracking-widest shadow-[0_0_10px_rgba(255,107,0,0.3)]"
                      >
                        <Flame className="h-3 w-3" fill="#FF6B00" strokeWidth={0} />
                        {b.streak_count}x streak
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-display font-black text-lg text-[#FF007F] flex items-center gap-1 justify-end">
                    <Zap className="h-3.5 w-3.5" fill="#FF007F" strokeWidth={0} />
                    ${(b.total_cents / 100).toFixed(2)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
